import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, CreditCard } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { BillingToggle, PriceLine, planKey } from "@/components/BillingToggle";
import { ChangePlanDialog } from "@/components/ChangePlanDialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const STATUS = {
  trialing: ["Free trial", "bg-teal-soft text-teal"],
  active: ["Active", "bg-teal text-white"],
  expired: ["Not active", "bg-red-100 text-red-800"],
};

const BASIC = [
  "Everything in Free",
  "Branded church assessment link + QR code",
  "Church administrator dashboard",
  "Volunteer assessment records and PDF results",
  "Ministry matching recommendations",
  "Customizable ministry teams",
  "Volunteer onboarding and approval workflow",
];
export const PERKS = { monthly: BASIC, pro: [
  "Everything in Basic",
  "Classic & Studio Themes — Customize your dashboard and volunteer assessment experience",
  "Planning Center People integration",
  "Staff accounts with admin and view-only permissions",
  "Advanced volunteer reporting and filters",
  "Church URL settings — old links keep working",
  "English + Spanish links, AI summaries & serving coach",
] };

export const PlanCard = ({ id, plan, highlight, onStripe, onPaypal, paypalEnabled, busy, current, locked, lockedText, code, setCode, stripeOnly, onChangePlan }) => (
  <div className={`rounded-2xl p-8 lift flex flex-col ${highlight ? "bg-navy text-white" : "card-soft"}`} data-testid={`plan-${id}`}>
    <div className="flex items-center justify-between">
      <p className={`font-display text-lg font-semibold ${highlight ? "" : "text-navy"}`}>{plan.label}</p>
      {highlight && <span className="text-xs bg-[#8FD3C6] text-navy rounded-full px-3 py-1 font-medium">Most complete</span>}
    </div>
    <PriceLine amount={plan.amount} interval={plan.interval} light={highlight} testid={`plan-${id}-price`} />
    <ul className="space-y-2 mt-6 flex-1">
      {PERKS[id].map((p) => <li key={p} className={`flex gap-2 text-sm ${highlight ? "text-white/80" : "text-muted-ink"}`}><Check size={16} className="text-teal shrink-0 mt-0.5" />{p}</li>)}
    </ul>
    {current ? (
      <p className="mt-8 text-sm font-medium" data-testid={`plan-${id}-current`}>Your current plan</p>
    ) : locked && onChangePlan ? (
      <button onClick={onChangePlan} disabled={busy} className={`${highlight ? "btn-ghost !text-navy" : "btn-navy"} mt-8 justify-center`} data-testid={`plan-${id}-change-btn`}>Switch to this plan</button>
    ) : locked ? (
      <p className={`mt-8 text-sm ${highlight ? "text-white/60" : "text-muted-ink"}`} data-testid={`plan-${id}-locked`}>{lockedText}</p>
    ) : (
      <div className="space-y-3 mt-8">
        <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Discount code (optional)"
          className={`h-11 font-mono-dt ${highlight ? "bg-white/10 border-white/30 text-white placeholder:text-white/50" : ""}`} data-testid={`plan-${id}-code-input`} />
        <button disabled={busy} onClick={onStripe} data-testid={`stripe-${id}-btn`}
          className={`w-full flex items-center justify-center gap-2 ${highlight ? "btn-ghost !text-navy" : "btn-navy"}`}><CreditCard size={16} /> Pay with card</button>
        {!stripeOnly && <button disabled={busy || !paypalEnabled} onClick={onPaypal} data-testid={`paypal-${id}-btn`}
          className="w-full rounded-[10px] py-3 font-semibold bg-[#FFC439] text-[#003087] transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-50">
          Pay with Pay<span className="text-[#009cde]">Pal</span>
        </button>}
      </div>
    )}
  </div>
);

const FreeCard = () => (
  <div className="card-soft p-8 lift flex flex-col" data-testid="plan-free">
    <p className="font-display text-lg font-semibold text-navy">Free</p>
    <p className="font-display text-5xl font-bold mt-4 text-navy">$0</p>
    <ul className="space-y-2 mt-6 flex-1">
      {["DISC Personality Assessment", "DISC personality results and explanations", "Downloadable DISC results PDF"].map((p) => (
        <li key={p} className="flex gap-2 text-sm text-muted-ink"><Check size={16} className="text-teal shrink-0 mt-0.5" />{p}</li>))}
    </ul>
    <Link to="/free-assessment" target="_blank" className="btn-ghost text-center mt-6" data-testid="free-assessment-btn">Take the Free DISC Assessment</Link>
  </div>
);

export default function Billing() {
  const [b, setB] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [codes, setCodes] = useState({ monthly: "", pro: "" });
  const [interval, setBillingInterval] = useState("year");
  const [changeTo, setChangeTo] = useState(null);
  const { refresh } = useAuth();
  useEffect(() => { api.get("/billing").then((r) => { setB(r.data); if (r.data.interval === "month") setBillingInterval("month"); }); }, []);

  const portal = async () => {
    setBusy(true);
    try { window.location.href = (await api.post("/billing/portal", { origin_url: window.location.origin })).data.url; }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't open billing management"); setBusy(false); }
  };
  const cancel = async () => {
    setConfirmCancel(false);
    setBusy(true);
    try {
      const { data } = await api.post("/billing/cancel");
      setB((p) => ({ ...p, ...data }));
      refresh();
      toast.success(data.cancel_at_period_end ? "Cancellation scheduled for the end of your paid period" : "Subscription canceled");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not cancel");
    } finally {
      setBusy(false);
    }
  };

  const go = async (provider, id) => {
    setBusy(true);
    const plan = planKey(id, interval);
    try {
      const code = codes[id].trim();
      const { data } = await api.post(`/billing/${provider === "stripe" ? "stripe/checkout" : "paypal/subscribe"}`, { plan, origin_url: window.location.origin, code: code || null });
      if (data.redeemed) {
        setB((p) => ({ ...p, ...data.billing }));
        refresh();
        toast.success("Code applied. Your church now has free access.");
        setBusy(false);
        return;
      }
      window.location.href = data.checkout_url || data.approve_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
      setBusy(false);
    }
  };

  if (!b) return null;
  const [label, cls] = STATUS[b.status];
  return (
    <div className="fade-up">
      <p className="eyebrow">Billing</p>
      <h1 className="font-display text-navy text-4xl font-semibold tracking-tight mt-2">Your plan</h1>
      <div className="card-soft p-7 mt-8 flex flex-wrap items-center gap-4" data-testid="billing-status-card">
        <span className={`rounded-full px-3 py-1 text-sm font-medium ${cls}`} data-testid="billing-status">{label}</span>
        <p className="text-muted-ink">
          {b.status === "active" && b.provider === "code" && <span data-testid="billing-complimentary">Complimentary <strong className="text-navy">{b.pro ? "Pro" : "Basic"}</strong> — 100% discount {b.comp_until ? <>until <strong className="text-navy">{new Date(b.comp_until).toLocaleDateString()}</strong></> : <strong className="text-navy">forever</strong>}. No card on file and no charges.</span>}
          {b.status === "active" && b.provider !== "code" && <span data-testid="billing-plan-summary">Subscribed to <strong className="text-navy">{b.plans[b.plan]?.label || b.plan}</strong>, billed <strong className="text-navy">{b.interval === "year" ? "yearly" : "monthly"}</strong> via {b.provider === "paypal" ? "PayPal" : "card"}{b.pro_until && !["pro", "pro_yearly"].includes(b.plan) && <> Pro features stay on until <strong className="text-navy">{new Date(b.pro_until).toLocaleDateString()}</strong>, then you move to Basic</>}{b.renews_at && <>. {b.cancel_at_period_end ? "Ends" : "Renews"} <strong className="text-navy">{new Date(b.renews_at).toLocaleDateString()}</strong></>}.</span>}
          {b.status === "active" && b.provider === "stripe" && <button onClick={portal} disabled={busy} className="ml-3 text-sm text-navy underline underline-offset-4" data-testid="manage-billing-btn">Manage billing, payment method &amp; invoices</button>}
          {b.payment_issue && <span className="block mt-2 text-red-700" data-testid="payment-issue-note">Your last payment didn't go through. Please update your payment method to keep your plan active.</span>}
          {b.status === "active" && b.provider !== "code" && !b.cancel_at_period_end && <button onClick={() => setConfirmCancel(true)} disabled={busy} className="ml-3 text-sm text-red-700 underline underline-offset-4" data-testid="cancel-subscription-btn">Cancel subscription</button>}
          {b.status === "trialing" && <><strong className="text-navy">{b.days_left} days</strong> left in your free trial. Subscribe now and you won't be charged until it ends.</>}
          {b.status === "expired" && <>Choose a plan below to unlock your dashboard and volunteer link. Have a discount code? Type it on a plan card before you pay.</>}
        </p>
      </div>
      <div className="mt-8 flex justify-center"><BillingToggle interval={interval} onChange={setBillingInterval} /></div>
      <div className="grid md:grid-cols-3 gap-6 mt-6">
        <FreeCard />
        {["monthly", "pro"].map((id) => (
          <PlanCard key={id} id={id} plan={b.plans[planKey(id, interval)]} highlight={id === "pro"} busy={busy} paypalEnabled={b.paypal_enabled}
            current={b.status === "active" && b.plan === planKey(id, interval)} locked={b.status === "active"} lockedText={b.provider === "code" ? "Included free with your discount code." : "Your current plan keeps renewing as it is."}
            onChangePlan={b.status === "active" && b.provider === "stripe" ? () => setChangeTo(planKey(id, interval)) : null} onStripe={() => go("stripe", id)} onPaypal={() => go("paypal", id)}
            code={codes[id]} setCode={(v) => setCodes((p) => ({ ...p, [id]: v }))} />
        ))}
      </div>
      <ChangePlanDialog plan={changeTo} onClose={() => setChangeTo(null)} onDone={(billing) => { setB((p) => ({ ...p, ...billing })); refresh(); }} />
      <AlertDialog open={confirmCancel} onOpenChange={setConfirmCancel}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel your subscription?</AlertDialogTitle>
            <AlertDialogDescription>{b?.provider === "stripe" ? "You won't be charged again. Your plan stays active until the end of the period you've already paid for." : "Billing stops right away and your dashboard and assessment link will be paused until you choose a plan again."}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-keep-btn">Keep plan</AlertDialogCancel>
            <AlertDialogAction onClick={cancel} data-testid="cancel-confirm-btn">Cancel subscription</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
